/**
 * Le tableau de bord de l'élève (docs/12 §6).
 *
 * Trois sections là où le document en annonce six. Les trois absentes ne sont
 * pas oubliées : rien ne les alimenterait — aucune mesure de durée n'est prise,
 * les certificats sont en phase 10, `messaging` est un squelette —, et une
 * carte affichant « 0 min » à vie serait pire qu'une carte absente.
 */
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import {
  useLearnerDashboard,
  type PendingResult,
  type RecentResult,
} from "@/features/learning/api/useDashboards";
import { Alert } from "@/shared/ui/Alert";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { Skeleton } from "@/shared/ui/Skeleton";

export function LearnerDashboardPage() {
  const { t } = useTranslation("learning");
  const dashboard = useLearnerDashboard();

  if (dashboard.isPending) {
    return <Skeleton className="mx-auto my-8 h-64 w-full max-w-4xl" label={t("dashboard.title")} />;
  }
  if (dashboard.isError) {
    return (
      <div className="mx-auto max-w-4xl px-4 py-8">
        <ErrorState error={dashboard.error} />
      </div>
    );
  }

  const { enrollments, awaiting_grading: waiting, recent_results: results } = dashboard.data;

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 px-4 py-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">{t("dashboard.title")}</h1>
        <p className="text-slate-600 dark:text-slate-400">{t("dashboard.subtitle")}</p>
      </header>

      <Card title={t("dashboard.inProgress")}>
        {enrollments.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">{t("mine.empty")}</p>
        ) : (
          <ul className="space-y-3">
            {enrollments.map((enrollment) => (
              <li key={enrollment.id} className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-medium">{enrollment.course_title}</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {t("player.progress", {
                      done: enrollment.completed_lesson_count,
                      total: enrollment.lesson_count,
                      percent: enrollment.progress_pct,
                    })}
                  </p>
                </div>
                <Link
                  to="/learn/$slug"
                  params={{ slug: enrollment.course_slug }}
                  className="text-brand-600 text-sm hover:underline"
                >
                  {enrollment.completed_at === null ? t("mine.resume") : t("mine.review")}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title={t("dashboard.awaiting")}>
        {waiting.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t("dashboard.awaitingEmpty")}
          </p>
        ) : (
          <ul className="space-y-3">
            {waiting.map((item) => (
              <li key={item.attempt_id}>
                <WaitingRow item={item} />
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card title={t("dashboard.recent")}>
        {results.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">{t("dashboard.recentEmpty")}</p>
        ) : (
          <ul className="space-y-2">
            {results.map((result) => (
              <li key={result.attempt_id}>
                <ResultRow result={result} />
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

/**
 * Une copie en attente.
 *
 * `is_blocking` change le ton et non seulement le texte : une copie qui retient
 * l'élève sur la suite du cours n'est pas une nouvelle neutre, et lui laisser
 * croire qu'il peut avancer serait le laisser chercher une porte fermée.
 */
function WaitingRow({ item }: { item: PendingResult }) {
  const { t } = useTranslation("learning");
  return (
    <Alert tone={item.is_blocking ? "warning" : "info"}>
      <p className="font-medium">{item.exercise_title}</p>
      <p className="text-sm">
        {item.is_blocking
          ? t("dashboard.awaitingBlocking", { course: item.course_title })
          : t("dashboard.awaitingItem", { course: item.course_title })}
      </p>
    </Alert>
  );
}

/**
 * Un résultat.
 *
 * `passed === null` n'existe pas ici — la section ne montre que les copies
 * conclues —, mais le cas est traité quand même : la valeur est un booléen
 * **nullable** dans le contrat, et un `passed ? … : …` afficherait « échoué »
 * pour une copie en attente si la section changeait un jour de filtre.
 */
function ResultRow({ result }: { result: RecentResult }) {
  const { t } = useTranslation("learning");
  const tone =
    result.passed === null ? "text-slate-500" : result.passed ? "text-green-700" : "text-red-700";
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
      <span>
        <span className="font-medium">{result.exercise_title}</span>
        <span className="text-slate-500 dark:text-slate-400"> — {result.course_title}</span>
      </span>
      <span className={tone}>
        {result.score_pct === null
          ? t("dashboard.noScore")
          : t("attempt.percent", { value: result.score_pct })}
      </span>
    </div>
  );
}
